/**
 * Message — renders one chat message bubble.
 *
 * User messages: right-aligned, accent background, plain text.
 * Assistant messages: full-width, markdown-rendered, tool calls inline.
 *
 * Markdown rendering uses react-markdown with the gfm plugin for tables
 * (the agent loves tables when comparing reps or products).
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ToolCallCard } from "./ToolCallCard";
import type { ChatMessage } from "./useChatStream";

interface Props {
  message: ChatMessage;
}

export function Message({ message }: Props) {
  if (message.role === "user") {
    return <UserMessage message={message} />;
  }
  return <AssistantMessage message={message} />;
}

function UserMessage({ message }: { message: ChatMessage }) {
  return (
    <div className="flex justify-end mb-5">
      <div
        className="max-w-[80%] rounded-lg px-4 py-2.5 t-base"
        style={{
          background: "var(--accent-soft)",
          color: "var(--accent-ink)",
        }}
      >
        <p className="text-[15px] leading-relaxed font-serif whitespace-pre-wrap">
          {message.text}
        </p>
      </div>
    </div>
  );
}

function AssistantMessage({ message }: { message: ChatMessage }) {
  const hasContent = message.text || message.toolCalls.length > 0 || message.error;
  const isWaiting = message.isStreaming && !hasContent;

  return (
    <div className="mb-6 view-in">
      {/* Tool calls (inline, in order) */}
      {message.toolCalls.map((tc) => (
        <ToolCallCard key={tc.id} tool={tc} />
      ))}

      {/* Streamed text */}
      {message.text && (
        <div
          className="prose prose-sm max-w-none font-serif text-[15.5px] leading-relaxed"
          style={{ color: "var(--ink)" }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {message.text}
          </ReactMarkdown>
          {message.isStreaming && <BlinkingCursor />}
        </div>
      )}

      {/* "Thinking..." placeholder while we wait for the first token */}
      {isWaiting && (
        <div
          className="text-[14px] font-serif italic tool-pulse"
          style={{ color: "var(--ink-3)" }}
        >
          Thinking…
        </div>
      )}

      {/* Error state */}
      {message.error && (
        <div
          className="mt-2 text-[13px] font-mono p-3 rounded-md"
          style={{
            background: "var(--oxblood-bg)",
            color: "var(--oxblood)",
            border: "1px solid var(--oxblood)",
          }}
        >
          {message.error}
        </div>
      )}

      {/* Usage footer (subtle, after the message) */}
      {!message.isStreaming && message.usage && (
        <UsageFooter usage={message.usage} rounds={message.rounds} />
      )}
    </div>
  );
}

function BlinkingCursor() {
  return (
    <span
      className="inline-block w-[8px] h-[15px] ml-0.5 align-middle tool-pulse"
      style={{ background: "var(--accent)" }}
    />
  );
}

function UsageFooter({
  usage,
  rounds,
}: {
  usage: ChatMessage["usage"];
  rounds: ChatMessage["rounds"];
}) {
  if (!usage) return null;
  const inTokens = usage.input_tokens ?? 0;
  const outTokens = usage.output_tokens ?? 0;
  const cacheRead = usage.cache_read_input_tokens ?? 0;
  const cacheWrite = usage.cache_creation_input_tokens ?? 0;

  return (
    <div
      className="mt-3 text-[10.5px] font-mono uppercase tracking-[0.12em]"
      style={{ color: "var(--ink-4)" }}
      title={`Cache reads: ${cacheRead}, cache writes: ${cacheWrite}`}
    >
      {rounds ?? 1} round{(rounds ?? 1) > 1 ? "s" : ""} ·{" "}
      {inTokens.toLocaleString()} in · {outTokens.toLocaleString()} out
      {cacheRead > 0 && ` · ${cacheRead.toLocaleString()} cached`}
    </div>
  );
}

/* react-markdown component overrides — keep styling consistent with the rest
 * of the app and prevent default prose plugin from importing fonts we don't use. */
const markdownComponents = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  p: ({ children }: any) => <p className="my-2">{children}</p>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  strong: ({ children }: any) => (
    <strong style={{ color: "var(--ink)", fontWeight: 600 }}>{children}</strong>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ul: ({ children }: any) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ol: ({ children }: any) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: ({ children }: any) => (
    <div className="my-3 overflow-x-auto">
      <table className="text-[13.5px] font-sans border-collapse min-w-full">
        {children}
      </table>
    </div>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  th: ({ children }: any) => (
    <th
      className="text-left px-3 py-1.5 font-medium border-b"
      style={{ borderColor: "var(--line)", color: "var(--ink-2)" }}
    >
      {children}
    </th>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  td: ({ children }: any) => (
    <td
      className="px-3 py-1.5 border-b"
      style={{ borderColor: "var(--line-2)" }}
    >
      {children}
    </td>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  code: ({ inline, children }: any) =>
    inline ? (
      <code
        className="px-1 py-0.5 rounded font-mono text-[12.5px]"
        style={{ background: "var(--bg-elev)", color: "var(--ink)" }}
      >
        {children}
      </code>
    ) : (
      <pre
        className="p-3 rounded-md my-3 overflow-x-auto font-mono text-[12.5px] leading-relaxed"
        style={{ background: "var(--bg-elev)", color: "var(--ink)" }}
      >
        <code>{children}</code>
      </pre>
    ),
};
